import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";
import { useNotifications } from "../api/chat";
import { ApiError, type Me } from "../api/client";
import { meQuery, useMe } from "../api/hooks";
import { preloadComposer } from "../components/chat/LazyComposer";
import { ConnectionBar } from "../components/shell/ConnectionBar";
import { ListColumn } from "../components/shell/Lists";
import { NotificationToasts } from "../components/shell/Notifications";
import { usePhoneFocus, usePhoneView, usePlace } from "../components/shell/nav";
import { Palette } from "../components/shell/Palette";
import { Rail } from "../components/shell/Rail";
import { setLang } from "../i18n";
import { placeToRestore, rememberPlace } from "../lib/lastPlace";
import { lazyRetry } from "../lib/lazy";
import { setTimeZone } from "../lib/time";
import { ONE_PANE, PHONE, useMedia } from "../lib/useMedia";
import { useRealtime } from "../realtime/wire";
import { usePreviewer } from "../state/previewer";

// The previewer (and the sheet grid, the Markdown renderer it uses) in a chunk of its own, not
// the shell's: loaded after the first paint, mounted while a file is open.
const loadPreviewer = () =>
  import("../components/chat/FilePreviewer").then((m) => ({ default: m.FilePreviewer }));
const FilePreviewer = lazyRetry(loadPreviewer, () => null);
function preloadPreviewer() {
  void loadPreviewer().catch(() => undefined);
}

/** The signed-in shell: the rail, the place's list, the page. Anyone not signed in goes to
 *  /signin. */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    let me: Me;
    try {
      me = await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { next: location.href } });
      }
      throw e;
    }
    // P2-A8: the app started at bare `/` (an installed app coming back, a reload) opens on the
    // last place this device was, before New chat shows; a link or a notification wins.
    if (startingNow() && location.pathname === "/" && !location.searchStr) {
      const place = await placeToRestore(context.queryClient, me);
      if (place) {
        const [to, query = ""] = place.split("?");
        throw redirect({
          to: to as "/",
          search: Object.fromEntries(new URLSearchParams(query)) as never,
          replace: true,
        });
      }
    }
  },
  component: Shell,
});

let starting = true;

/** True once: for the app's first navigation in this page. */
function startingNow(): boolean {
  const first = starting;
  starting = false;
  return first;
}

/** Each place the person goes is the device's last place (P2-A8), except a start at bare `/`
 *  (nothing chosen yet: the stored place, or its absence, stands). */
function useLastPlace() {
  const at = useRouterState({ select: (s) => `${s.location.pathname}|${s.location.searchStr}` });
  const first = useRef(true);
  useEffect(() => {
    const [pathname = "/", search = ""] = at.split("|");
    const blank = first.current && pathname === "/" && !search;
    first.current = false;
    if (!blank) rememberPlace(pathname, search);
  }, [at]);
}

function Shell() {
  const me = useMe();
  if (!me.data) return null;
  return <SignedIn me={me.data} />;
}

/** What's waiting, where it's seen without opening the app (R10-Q7): "(3) Vantage" in the
 *  tab, and the installed app's icon badge where the browser offers one. */
function useUnreadMarks() {
  const notifications = useNotifications();
  const n = notifications.data?.unread ?? 0;
  useEffect(() => {
    document.title = n > 0 ? `(${n > 99 ? "99+" : n}) Vantage` : "Vantage";
    const nav = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    const done = n > 0 ? nav.setAppBadge?.(n) : nav.clearAppBadge?.();
    void done?.catch(() => undefined); // no permission, or no badge on this device
  }, [n]);
}

/** A file dropped anywhere but a composer does nothing (R10-Q5): the browser would otherwise
 *  open it in place of the app. */
function useFileDropGuard() {
  useEffect(() => {
    const guard = (e: DragEvent) => {
      if (!e.defaultPrevented && Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
      }
    };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => {
      window.removeEventListener("dragover", guard);
      window.removeEventListener("drop", guard);
    };
  }, []);
}

/** R7.5 the calm shell (STATUS "R7.5 contract" 2–4; design/mockup-r7.5.html): the rail at the
 *  left (a tab bar on phones), the current place's list column (none on Automations), then the
 *  page. On a phone the list and the page show one at a time (`data-phone`). */
function SignedIn({ me }: { me: Me }) {
  // Times in the person's own zone (R10-Q6); the page's language for screen readers and
  // hyphenation follows the interface language (D31).
  setTimeZone(me.effective_timezone);
  // The interface language (D31, R10-Q16): set before anything renders its words, and the
  // shell below mounts again when it changes, so every word follows.
  setLang(me.ui_language);
  useRealtime(me);
  useUnreadMarks();
  useFileDropGuard();
  useLastPlace();
  const place = usePlace();
  const view = usePhoneView(place);
  const phone = useMedia(PHONE);
  // iPad portrait as a phone: the list or the page (R10-T3); the rail stays a rail there.
  const onePane = useMedia(ONE_PANE);
  usePhoneFocus(onePane, view);
  // The composer's editor, fetched after the first paint (a phone's list shows first).
  useEffect(() => {
    const t = setTimeout(preloadComposer, 0);
    return () => clearTimeout(t);
  }, []);
  // The file previewer's code, fetched after the first paint too: it's mounted only while a
  // file is open (R8 task 10), and opens at once.
  useEffect(() => {
    const t = setTimeout(preloadPreviewer, 0);
    return () => clearTimeout(t);
  }, []);
  const previewing = usePreviewer((s) => s.open !== null);
  // Somewhere else now (a link, a notification): an open file belonged to the last place.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (pathname) usePreviewer.getState().close();
  }, [pathname]);
  // A phone showing the list doesn't keep the page mounted underneath: a hidden conversation
  // would still mark its messages read (the feed starts "at the bottom") and keep the person
  // "viewing" it, so its unread dots and counts would vanish unseen (R7.5 review).
  const page = onePane && view === "list" ? null : <Outlet />;
  return (
    <div key={me.ui_language} className="app view on" id="app" data-place={place} data-phone={view}>
      <Rail me={me} place={place} phone={phone} />
      <ListColumn me={me} place={place} phone={phone} />
      {page}
      <Palette me={me} />
      {/* R8 task 10: the file previewer, one for every place a file card shows. */}
      {previewing && (
        <Suspense fallback={null}>
          <FilePreviewer />
        </Suspense>
      )}
      <NotificationToasts />
      <ConnectionBar />
    </div>
  );
}
